import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import i18n from '@/i18n';
import api from '@/lib/api';
import toast from 'react-hot-toast';
import type { Organization } from '@/types';
import PageSkeleton from '@/components/PageSkeleton';
import BrandingPage from './BrandingPage';
import TermsAndConditionsEditor from './TermsAndConditionsEditor';

const LANGUAGES = [
  { code: 'en', label: 'English' },
  { code: 'fr', label: 'Français' },
  { code: 'nl', label: 'Nederlands' },
  { code: 'es', label: 'Español' },
] as const;

export default function SettingsPage() {
  const { t } = useTranslation();
  const [tab, setTab] = useState<'org' | 'billing' | 'profile' | 'branding' | 'terms'>('profile');
  const [org, setOrg] = useState<Organization | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    async function load() {
      try {
        const orgRes = await api.get('/organizations/current');
        setOrg(orgRes.data.data);
      } catch { /* ignore */ }
      setLoading(false);
    }
    load();
  }, []);

  async function handleSaveOrg(e: React.FormEvent) {
    e.preventDefault();
    if (!org) return;
    setSaving(true);
    try {
      const res = await api.put('/organizations/current', org);
      setOrg(res.data.data);
      toast.success(t('common.saved'));
    } catch {
      toast.error(t('common.error'));
    }
    setSaving(false);
  }

  if (loading) return <PageSkeleton variant="detail" />;

  const tabs = [
    { key: 'profile', label: t('settings.profile') },
    { key: 'org', label: t('settings.organization') },
    { key: 'billing', label: t('settings.billing') },
    { key: 'branding', label: t('branding.title') },
    { key: 'terms', label: t('termsManagement.title') },
  ] as const;

  const handleLanguageChange = (lang: string) => {
    i18n.changeLanguage(lang);
    localStorage.setItem('ledgora-lang', lang);
  };

  return (
    <div>
      <h1 className="text-2xl font-bold mb-6">{t('settings.title')}</h1>

      <div className="flex gap-1 mb-6 border-b overflow-x-auto -mx-4 px-4 sm:mx-0 sm:px-0">
        {tabs.map((tb) => (
          <button
            key={tb.key}
            onClick={() => setTab(tb.key)}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition whitespace-nowrap ${tab === tb.key ? 'border-primary-500 text-primary-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
          >
            {tb.label}
          </button>
        ))}
      </div>

      {/* Profile */}
      {tab === 'profile' && (
        <div className="card card-body max-w-2xl space-y-6">
          <div>
            <h3 className="text-lg font-semibold mb-4">{t('settings.languageSection')}</h3>
            <label className="label">{t('settings.language')}</label>
            <div className="flex gap-3 mt-2">
              {LANGUAGES.map((lang) => (
                <button
                  key={lang.code}
                  onClick={() => handleLanguageChange(lang.code)}
                  className={`px-4 py-2 rounded-lg border text-sm font-medium transition ${
                    i18n.language === lang.code
                      ? 'border-primary-500 bg-primary-50 text-primary-700'
                      : 'border-gray-300 text-gray-600 hover:border-gray-400 hover:bg-gray-50'
                  }`}
                >
                  {lang.label}
                </button>
              ))}
            </div>
            <p className="text-sm text-gray-500 mt-2">{t('settings.languageHint')}</p>
          </div>
        </div>
      )}

      {/* Organization */}
      {tab === 'org' && org && (
        <form onSubmit={handleSaveOrg} className="card card-body max-w-2xl space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <label className="label">{t('settings.companyName')} *</label>
              <input type="text" className="input" value={org.name} onChange={(e) => setOrg({ ...org, name: e.target.value })} required />
            </div>
            <div>
              <label className="label">{t('settings.vatNumber')}</label>
              <input type="text" className="input" value={org.vatNumber || ''} onChange={(e) => setOrg({ ...org, vatNumber: e.target.value })} placeholder="BE0123456789" />
            </div>
            <div>
              <label className="label">{t('settings.enterpriseNumber')}</label>
              <input type="text" className="input" value={org.enterpriseNumber || ''} onChange={(e) => setOrg({ ...org, enterpriseNumber: e.target.value })} />
            </div>
            <div>
              <label className="label">{t('settings.email')}</label>
              <input type="email" className="input" value={org.email || ''} onChange={(e) => setOrg({ ...org, email: e.target.value })} />
            </div>
            <div>
              <label className="label">{t('settings.phone')}</label>
              <input type="text" className="input" value={org.phone || ''} onChange={(e) => setOrg({ ...org, phone: e.target.value })} />
            </div>
            <div className="md:col-span-2">
              <label className="label">{t('settings.address')}</label>
              <input type="text" className="input" value={org.address || ''} onChange={(e) => setOrg({ ...org, address: e.target.value })} />
            </div>
            <div>
              <label className="label">{t('settings.city')}</label>
              <input type="text" className="input" value={org.city || ''} onChange={(e) => setOrg({ ...org, city: e.target.value })} />
            </div>
            <div>
              <label className="label">{t('settings.postalCode')}</label>
              <input type="text" className="input" value={org.postalCode || ''} onChange={(e) => setOrg({ ...org, postalCode: e.target.value })} />
            </div>
            <div>
              <label className="label">{t('settings.country')}</label>
              <input type="text" className="input" maxLength={2} value={org.countryCode || ''} onChange={(e) => setOrg({ ...org, countryCode: e.target.value.toUpperCase() })} />
            </div>
          </div>
          <div className="flex justify-end pt-2">
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? t('common.saving') : t('common.save')}
            </button>
          </div>
        </form>
      )}

      {/* Billing */}
      {tab === 'billing' && org && (
        <form onSubmit={handleSaveOrg} className="card card-body max-w-2xl space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="label">{t('settings.iban')}</label>
              <input type="text" className="input font-mono" value={org.iban || ''} onChange={(e) => setOrg({ ...org, iban: e.target.value })} placeholder="BE68 5390 0754 7034" />
            </div>
            <div>
              <label className="label">{t('settings.bic')}</label>
              <input type="text" className="input font-mono" value={org.bic || ''} onChange={(e) => setOrg({ ...org, bic: e.target.value })} />
            </div>
            <div>
              <label className="label">{t('settings.defaultVatRate')}</label>
              <select className="input" value={org.defaultVatRate || '21.00'} onChange={(e) => setOrg({ ...org, defaultVatRate: e.target.value })}>
                <option value="21.00">21%</option>
                <option value="12.00">12%</option>
                <option value="6.00">6%</option>
                <option value="0.00">0%</option>
              </select>
            </div>
            <div>
              <label className="label">{t('settings.defaultPaymentTermDays')}</label>
              <input type="number" className="input" value={org.defaultPaymentTermDays || 30} onChange={(e) => setOrg({ ...org, defaultPaymentTermDays: Number(e.target.value) })} />
            </div>
            <div>
              <label className="label">{t('settings.invoicePrefix')}</label>
              <input type="text" className="input" value={org.invoicePrefix || ''} onChange={(e) => setOrg({ ...org, invoicePrefix: e.target.value })} placeholder="INV" />
            </div>
            <div>
              <label className="label">{t('settings.quotePrefix')}</label>
              <input type="text" className="input" value={org.quotePrefix || ''} onChange={(e) => setOrg({ ...org, quotePrefix: e.target.value })} placeholder="QT" />
            </div>
          </div>
          <div className="flex justify-end pt-2">
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? t('common.saving') : t('common.save')}
            </button>
          </div>
        </form>
      )}

      {/* Branding */}
      {tab === 'branding' && (
        <BrandingPage />
      )}

      {/* Terms & Conditions */}
      {tab === 'terms' && (
        <TermsAndConditionsEditor />
      )}

    </div>
  );
}
